import React from 'react';
import PropTypes from 'prop-types';
import { AgencyPage } from '../../../components/Views/AgencyPage';
import Layout from '../../../components/Layout/Layout';
import initApollo from '../../../utils/init-Apollo';
import { getStaticData } from '../../../utils/helpers/getStaticData';
import { getAgencySeoData } from '../../../utils/helpers/seo';
import { getSelectedAgencyQuery } from '../../../api/queries';
import { normalizePath } from '../../../utils/helpers';
import { withBugsnagGetStaticProps } from '../../../utils/withBugsnag';
import { AGENCY_REVALIDATE_SECONDS } from '../../../utils/constants/agencyPage';
import { loadAgencyMockData } from '../../../utils/helpers/loadAgencyMockData';
import { extractApolloState } from '../../../utils/helpers/extractApolloState';
import {
  getAgencyInitialItems,
  parseAgencySegments,
  SLUG_TO_DEAL_TYPE
} from '../../../utils/helpers/agencyPage';

const Agency = ({ agencySlug, hasError, initialFilter }) => (
  <AgencyPage
    agencySlug={agencySlug}
    hasError={hasError}
    initialFilter={initialFilter}
  />
);

export const getStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking'
});

const isE2E = process.env.NEXT_PUBLIC_E2E === '1';

export const getStaticProps = withBugsnagGetStaticProps(
  async ({ params, locale }) => {
    const slug = params.agency;
    const segments = Array.isArray(params.segments) ? params.segments : [];

    const asPath = `/agentlikler/${slug}${
      segments.length ? `/${segments.join('/')}` : ''
    }`;
    const newAsPath = normalizePath(asPath);

    const apolloClient = initApollo({}, { lang: locale });

    if (isE2E && process.env.NODE_ENV !== 'production') {
      try {
        await loadAgencyMockData(apolloClient);

        return extractApolloState(apolloClient, {
          props: {
            seoData: getAgencySeoData(
              'Real Əmlak Nizami',
              locale,
              newAsPath,
              segments
            ),
            agencySlug: 'realemlaknizami',
            currentLang: locale
          },
          revalidate: AGENCY_REVALIDATE_SECONDS
        });
      } catch (error) {
        console.error('Error loading mock data:', error);
      }
    }

    const [agencyRes] = await Promise.all([
      apolloClient.query({
        query: getSelectedAgencyQuery,
        variables: { slug },
        errorPolicy: 'all'
      }),
      getStaticData(apolloClient)
    ]);

    const agency = agencyRes?.data?.agency ?? null;
    const hasGraphQLErrors = Boolean(agencyRes?.errors?.length);

    if (!agency && hasGraphQLErrors) {
      return {
        props: {
          seoData: null,
          agencySlug: slug,
          hasError: true,
          currentLang: locale
        },
        revalidate: AGENCY_REVALIDATE_SECONDS
      };
    }

    if (!agency && !hasGraphQLErrors) {
      return {
        notFound: true,
        revalidate: AGENCY_REVALIDATE_SECONDS
      };
    }

    const { dealType, category } = parseAgencySegments(
      segments,
      agency.itemCategories
    );

    const dealSegmentCount = segments.filter(s =>
      Object.hasOwn(SLUG_TO_DEAL_TYPE, s)
    ).length;
    const nonDealSegments = segments.filter(
      s => !Object.hasOwn(SLUG_TO_DEAL_TYPE, s)
    );
    const isInvalidSegmentCount =
      dealSegmentCount > 1 || nonDealSegments.length > 1;
    const hasInvalidCategory = nonDealSegments.length > 0 && category === null;

    if (isInvalidSegmentCount || hasInvalidCategory) {
      return {
        redirect: {
          destination: `/agentlikler/${slug}`,
          permanent: false
        },
        revalidate: AGENCY_REVALIDATE_SECONDS
      };
    }

    if (segments.length) {
      const { totalCount } = await getAgencyInitialItems({
        apolloClient,
        agencyId: agency.id,
        dealType,
        category,
        itemCategories: agency.itemCategories
      });

      if (totalCount === 0) {
        const categorySlug = category
          ? agency.itemCategories
              .find(c => c.id === category)
              ?.slug?.split('/')
              .pop()
          : null;

        const destination =
          dealType && category && categorySlug
            ? `/agentlikler/${slug}/${categorySlug}`
            : `/agentlikler/${slug}`;

        return {
          redirect: { destination, permanent: false },
          revalidate: AGENCY_REVALIDATE_SECONDS
        };
      }
    }

    return extractApolloState(apolloClient, {
      props: {
        seoData: getAgencySeoData(agency?.name, locale, newAsPath, segments),
        agencySlug: slug,
        hasError: false,
        currentLang: locale,
        initialFilter: {
          dealType,
          category
        }
      },
      revalidate: AGENCY_REVALIDATE_SECONDS
    });
  },
  'agencies/[agency]/[[...section]]'
);

Agency.getLayout = (page, pageProps) => (
  <Layout
    isBottomNavigation={false}
    isBanner={true}
    isShowBackButton
    isShowCloseButton={false}
    pageProps={pageProps}
    hideMobileHeader
  >
    {page}
  </Layout>
);

Agency.propTypes = {
  agencySlug: PropTypes.string,
  hasError: PropTypes.bool,
  initialFilter: PropTypes.shape({
    dealType: PropTypes.string,
    category: PropTypes.string
  })
};

export default Agency;
